import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Plus, Trash, Pencil, MagnifyingGlass, UserSwitch, Eye,
  Laptop, Desktop, DeviceMobile, Snowflake, Fan, Armchair,
  VideoCamera, Keyboard, Printer, Package, House,
} from '@phosphor-icons/react';
import { usePagination } from '../components/TablePagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES_DEFAULT = ['Laptop', 'Desktop', 'Mobile', 'Air Conditioner', 'Fan',
  'Work Station', 'Chair', 'CCTV', 'Keyboard', 'Printer', 'Other'];

const ICON = {
  Laptop, Desktop, Mobile: DeviceMobile, 'Air Conditioner': Snowflake, Fan,
  'Work Station': House, Chair: Armchair, CCTV: VideoCamera, Keyboard, Printer, Other: Package,
};

const CAT_COLOR = {
  Laptop: '#0ea5e9', Desktop: '#6366f1', Mobile: '#ec4899', 'Air Conditioner': '#06b6d4',
  Fan: '#8b5cf6', 'Work Station': '#f59e0b', Chair: '#ef4444', CCTV: '#10b981',
  Keyboard: '#64748b', Printer: '#a855f7', Other: '#475569',
};

const STATUS_COLOR = {
  in_use:    { bg: '#dcfce7', color: '#15803d', label: 'IN USE' },
  in_stock:  { bg: '#dbeafe', color: '#1e40af', label: 'IN STOCK' },
  repair:    { bg: '#fef3c7', color: '#92400e', label: 'REPAIR' },
  damaged:   { bg: '#fee2e2', color: '#b91c1c', label: 'DAMAGED' },
  disposed:  { bg: '#f3e8ff', color: '#6b21a8', label: 'DISPOSED' },
};

const empty = () => ({
  name: '', category: 'Laptop', brand: '', model: '', serial_no: '',
  purchase_date: '', purchase_cost: 0, current_value: 0,
  condition: 'Good', warranty_expiry: '', status: 'in_stock',
  ram: '', storage: '', os: '',
  assigned_to: '', assigned_to_name: '', assigned_date: '', notes: '',
});

const fmtINR = (n) => '₹' + (Number(n) || 0).toLocaleString('en-IN');

const AssetsPage = () => {
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState(CATEGORIES_DEFAULT);
  const [activeCat, setActiveCat] = useState('Laptop');
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [assignFor, setAssignFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  // Advanced filters
  const [showFilters, setShowFilters] = useState(false);
  const [fAssignee, setFAssignee] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fCondition, setFCondition] = useState('');
  const [fPurchaseFrom, setFPurchaseFrom] = useState('');
  const [fPurchaseTo, setFPurchaseTo] = useState('');
  const [fWarrantyFrom, setFWarrantyFrom] = useState('');
  const [fWarrantyTo, setFWarrantyTo] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { category: activeCat };
      if (fAssignee) params.assigned_to = fAssignee;
      if (fStatus) params.status = fStatus;
      if (fCondition) params.condition = fCondition;
      if (fPurchaseFrom) params.purchase_from = fPurchaseFrom;
      if (fPurchaseTo) params.purchase_to = fPurchaseTo;
      if (fWarrantyFrom) params.warranty_from = fWarrantyFrom;
      if (fWarrantyTo) params.warranty_to = fWarrantyTo;
      const [sRes, aRes, eRes, cRes] = await Promise.all([
        axios.get(`${API_URL}/api/assets/summary`, { withCredentials: true }),
        axios.get(`${API_URL}/api/assets`, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/employees`, { withCredentials: true }),
        axios.get(`${API_URL}/api/assets/categories`, { withCredentials: true }).catch(() => ({ data: CATEGORIES_DEFAULT })),
      ]);
      setSummary(sRes.data);
      setItems(aRes.data || []);
      setEmployees(eRes.data || []);
      setCategories(cRes.data || CATEGORIES_DEFAULT);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCat, fAssignee, fStatus, fCondition, fPurchaseFrom, fPurchaseTo, fWarrantyFrom, fWarrantyTo]);

  const clearFilters = () => {
    setFAssignee(''); setFStatus(''); setFCondition('');
    setFPurchaseFrom(''); setFPurchaseTo(''); setFWarrantyFrom(''); setFWarrantyTo('');
    setSearch('');
  };
  const activeFilterCount = [fAssignee, fStatus, fCondition, fPurchaseFrom, fPurchaseTo, fWarrantyFrom, fWarrantyTo, search].filter(Boolean).length;

  const doExport = async (fmt) => {
    try {
      const r = await axios.get(`${API_URL}/api/assets/export`, {
        params: { format: fmt },
        responseType: 'blob',
        withCredentials: true,
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assets_${new Date().toISOString().slice(0, 10)}.${fmt === 'xlsx' ? 'xlsx' : 'csv'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const doImport = async (file) => {
    if (!file) return;
    setImportBusy(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API_URL}/api/assets/import`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(r.data);
      load();
    } catch (e) {
      setImportResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setImportBusy(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const s = search.toLowerCase();
    return items.filter((x) => [x.name, x.brand, x.model, x.serial_no, x.assigned_to_name]
      .some((f) => (f || '').toLowerCase().includes(s)));
  }, [items, search]);

  const totalValue = useMemo(() => items.reduce((a, b) => a + (Number(b.current_value) || 0), 0), [items]);

  const { paged: pagedAssets, controls: assetControls } = usePagination(filtered, { storageKey: 'assets-page-size', testIdPrefix: 'asset' });

  const startNew = () => { setForm({ ...empty(), category: activeCat }); setEditing({ id: null }); };
  const startEdit = (a) => { setForm({ ...empty(), ...a }); setEditing(a); };
  const cancel = () => { setEditing(null); setForm(empty()); };

  const save = async () => {
    if (!form.name.trim()) { alert('Asset name is required'); return; }
    try {
      if (editing?.id) await axios.put(`${API_URL}/api/assets/${editing.id}`, form, { withCredentials: true });
      else await axios.post(`${API_URL}/api/assets`, form, { withCredentials: true });
      cancel(); load();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this asset? Its assignment history will also be removed.')) return;
    await axios.delete(`${API_URL}/api/assets/${id}`, { withCredentials: true });
    load();
  };

  const doAssign = async (employee_id) => {
    try {
      await axios.post(`${API_URL}/api/assets/${assignFor.id}/assign`,
        { employee_id, note: '' }, { withCredentials: true });
      setAssignFor(null);
      load();
    } catch (e) { alert('Assignment failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const showHistory = async (asset) => {
    setHistoryFor(asset);
    try {
      const r = await axios.get(`${API_URL}/api/assets/${asset.id}/history`, { withCredentials: true });
      setHistory(r.data || []);
    } catch { setHistory([]); }
  };

  return (
    <div data-testid="assets-page" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--dx-text)' }}>Assets Management</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>
            {summary?.total || 0} total assets · Current category value: {fmtINR(totalValue)}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: '#94a3b8' }} />
            <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-7 w-48" data-testid="asset-search" />
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
            className={activeFilterCount ? 'border-blue-400 text-blue-700' : ''}>
            🎛️ Filters {activeFilterCount ? `(${activeFilterCount})` : ''}
          </Button>
          <Button variant="outline" size="sm" onClick={() => doExport('csv')} data-testid="export-csv-btn">📥 CSV</Button>
          <Button variant="outline" size="sm" onClick={() => doExport('xlsx')} data-testid="export-xlsx-btn">📥 Excel</Button>
          <label className="cursor-pointer">
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) { doImport(f); e.target.value = ''; } }}
              data-testid="import-file-input" />
            <span className="inline-flex items-center justify-center rounded-md text-sm font-medium h-9 px-3 border border-input bg-background hover:bg-accent">
              📤 Import
            </span>
          </label>
          <Button onClick={startNew} data-testid="new-asset-btn">
            <Plus size={14} className="mr-1.5" /> New Asset
          </Button>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <Card className="p-3" data-testid="filters-panel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-gray-500 mb-1 block">Assigned To</Label>
              <select value={fAssignee} onChange={(e) => setFAssignee(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-xs bg-white" data-testid="filter-assignee">
                <option value="">All</option>
                <option value="__unassigned__">— Unassigned —</option>
                {employees.filter((e) => e.is_active !== false).map((e) => (
                  <option key={e.id} value={e.id}>{e.name} ({e.emp_id})</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-gray-500 mb-1 block">Status</Label>
              <select value={fStatus} onChange={(e) => setFStatus(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-xs bg-white" data-testid="filter-status">
                <option value="">All</option>
                <option value="in_use">In Use</option>
                <option value="in_stock">In Stock</option>
                <option value="repair">Under Repair</option>
                <option value="damaged">Damaged</option>
                <option value="disposed">Disposed</option>
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-gray-500 mb-1 block">Condition</Label>
              <select value={fCondition} onChange={(e) => setFCondition(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-xs bg-white" data-testid="filter-condition">
                <option value="">All</option>
                <option value="Excellent">Excellent</option>
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
                <option value="Poor">Poor</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button variant="ghost" size="sm" onClick={clearFilters} disabled={!activeFilterCount}
                className="text-xs h-7" data-testid="clear-filters-btn">✕ Clear all filters</Button>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-wide text-gray-500 mb-1 block">Purchase Date Range</Label>
              <div className="flex gap-1 items-center">
                <Input type="date" value={fPurchaseFrom} onChange={(e) => setFPurchaseFrom(e.target.value)}
                  className="h-8 text-xs" data-testid="filter-purchase-from" />
                <span className="text-gray-400">→</span>
                <Input type="date" value={fPurchaseTo} onChange={(e) => setFPurchaseTo(e.target.value)}
                  className="h-8 text-xs" data-testid="filter-purchase-to" />
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-wide text-gray-500 mb-1 block">Warranty Expiry Range</Label>
              <div className="flex gap-1 items-center">
                <Input type="date" value={fWarrantyFrom} onChange={(e) => setFWarrantyFrom(e.target.value)}
                  className="h-8 text-xs" data-testid="filter-warranty-from" />
                <span className="text-gray-400">→</span>
                <Input type="date" value={fWarrantyTo} onChange={(e) => setFWarrantyTo(e.target.value)}
                  className="h-8 text-xs" data-testid="filter-warranty-to" />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Import progress / result */}
      {(importBusy || importResult) && (
        <Card className="p-3 text-xs" data-testid="import-result-card">
          {importBusy && <div className="text-gray-600">📤 Importing — please wait…</div>}
          {importResult && !importResult.error && (
            <div className="text-gray-800">
              <span className="font-bold">Import complete:</span>{' '}
              <span className="text-green-700">{importResult.added} added</span>,{' '}
              <span className="text-blue-700">{importResult.updated} updated</span>,{' '}
              <span className="text-amber-700">{importResult.skipped} skipped</span>
              {(importResult.new_categories || []).length > 0 && (
                <span className="ml-2 text-purple-700">+ {importResult.new_categories.length} new categories</span>
              )}
              {(importResult.errors || []).length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-amber-700">{importResult.errors.length} row errors</summary>
                  <ul className="ml-3 mt-1 text-amber-800">
                    {importResult.errors.slice(0, 10).map((er, i) => <li key={i}>• {er}</li>)}
                  </ul>
                </details>
              )}
              <button onClick={() => setImportResult(null)} className="ml-3 text-gray-500 underline">dismiss</button>
            </div>
          )}
          {importResult?.error && (
            <div className="text-red-700">
              ❌ Import failed: {importResult.error}{' '}
              <button onClick={() => setImportResult(null)} className="ml-2 text-gray-500 underline">dismiss</button>
            </div>
          )}
        </Card>
      )}

      {/* Category tiles */}
      <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-11 gap-2">
        {summary?.categories?.map((c) => {
          const Icon = ICON[c.category] || Package;
          const color = CAT_COLOR[c.category] || '#64748b';
          const active = activeCat === c.category;
          return (
            <button key={c.category} onClick={() => setActiveCat(c.category)} data-testid={`cat-${c.category.replace(/\s+/g, '-')}`}
              className="rounded-lg p-2 text-center transition-all"
              style={{
                background: active ? color : 'white',
                color: active ? 'white' : 'var(--dx-text)',
                border: `2px solid ${active ? color : '#e2e8f0'}`,
                boxShadow: active ? `0 4px 12px ${color}40` : 'none',
              }}>
              <Icon size={18} weight={active ? 'fill' : 'regular'} className="mx-auto" />
              <div className="text-[9px] uppercase tracking-tight mt-1 leading-tight">{c.category}</div>
              <div className="text-base font-black">{c.count}</div>
              <div className="text-[8px]" style={{ color: active ? 'rgba(255,255,255,.7)' : '#94a3b8' }}>{c.in_use} in use</div>
            </button>
          );
        })}
      </div>

      {/* Active category table */}
      <Card className="p-0 overflow-x-auto">
        <div className="px-4 py-2.5 text-sm font-semibold flex items-center justify-between" style={{ background: 'var(--dx-bg)' }}>
          <span>{activeCat}s ({filtered.length})</span>
          <span className="text-[10px] text-gray-500">Updated just now</span>
        </div>
        <table className="w-full text-sm">
          <thead style={{ background: 'var(--dx-bg)' }}>
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Name</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Brand / Model</th>
              {(activeCat === 'Laptop' || activeCat === 'Desktop') && (
                <>
                  <th className="text-left px-3 py-2 font-semibold text-xs uppercase">RAM</th>
                  <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Storage</th>
                </>
              )}
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Serial</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Condition</th>
              <th className="text-right px-3 py-2 font-semibold text-xs uppercase">Value</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Warranty</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Assigned To</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Status</th>
              <th className="text-right px-3 py-2 font-semibold text-xs uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="text-center py-8 text-[#52525B]">Loading…</td></tr>
            ) : pagedAssets.length === 0 ? (
              <tr><td colSpan={11} className="text-center py-8 text-[#52525B]">No {activeCat.toLowerCase()}s yet — click &ldquo;New Asset&rdquo; to add one.</td></tr>
            ) : pagedAssets.map((a) => {
              const st = STATUS_COLOR[a.status] || STATUS_COLOR.in_stock;
              return (
                <tr key={a.id} className="border-t hover:bg-gray-50" data-testid={`asset-row-${a.id}`}>
                  <td className="px-3 py-2 font-medium">{a.name}</td>
                  <td className="px-3 py-2 text-[12px]">{a.brand}{a.model ? ` — ${a.model}` : ''}</td>
                  {(activeCat === 'Laptop' || activeCat === 'Desktop') && (
                    <>
                      <td className="px-3 py-2 text-[12px]">{a.ram || '—'}</td>
                      <td className="px-3 py-2 text-[12px]">{a.storage || '—'}</td>
                    </>
                  )}
                  <td className="px-3 py-2 text-[11px] font-mono">{a.serial_no || '—'}</td>
                  <td className="px-3 py-2 text-[12px]">{a.condition}</td>
                  <td className="px-3 py-2 text-right text-[12px] font-semibold">{fmtINR(a.current_value)}</td>
                  <td className="px-3 py-2 text-[11px]">{a.warranty_expiry || '—'}</td>
                  <td className="px-3 py-2 text-[12px]">
                    {a.assigned_to_name ? (
                      <span className="font-semibold" style={{ color: '#15803d' }}>{a.assigned_to_name}</span>
                    ) : (
                      <span className="text-gray-400 italic">Not assigned</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" onClick={() => setAssignFor(a)} data-testid={`assign-${a.id}`}
                        className="text-white" style={{ background: a.assigned_to ? '#0284c7' : '#16a34a' }}>
                        <UserSwitch size={13} className="mr-1" /> {a.assigned_to ? 'Reassign' : 'Assign'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => showHistory(a)} title="History" data-testid={`history-${a.id}`}>
                        <Eye size={14} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => startEdit(a)} data-testid={`edit-${a.id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(a.id)} data-testid={`delete-${a.id}`}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {assetControls}
      </Card>

      {/* Form modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={cancel}>
          <Card className="w-full max-w-2xl bg-white p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">{editing.id ? 'Edit Asset' : 'New Asset'}</h2>
              <button onClick={cancel} className="text-2xl leading-none px-2">×</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="asset-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Category *</Label>
                <div className="flex gap-1">
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="flex-1 border rounded px-3 py-2 text-sm bg-white" data-testid="asset-cat-select">
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <Button type="button" size="sm" variant="outline"
                    onClick={async () => {
                      const name = window.prompt('New category name?');
                      if (!name?.trim()) return;
                      try {
                        const r = await axios.post(`${API_URL}/api/assets/categories`, { name: name.trim() }, { withCredentials: true });
                        setCategories(r.data?.items || categories);
                        setForm({ ...form, category: name.trim() });
                      } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
                    }}
                    data-testid="add-category-btn"
                    title="Add new category">
                    + New
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Brand</Label>
                <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="asset-brand-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Model</Label>
                <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Serial No.</Label>
                <Input value={form.serial_no} onChange={(e) => setForm({ ...form, serial_no: e.target.value })} />
              </div>
              {(form.category === 'Laptop' || form.category === 'Desktop') && (
                <>
                  <div className="space-y-1">
                    <Label className="text-xs">RAM</Label>
                    <Input value={form.ram} placeholder="16GB" onChange={(e) => setForm({ ...form, ram: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Storage</Label>
                    <Input value={form.storage} placeholder="512GB SSD" onChange={(e) => setForm({ ...form, storage: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">OS</Label>
                    <Input value={form.os} placeholder="Windows 11 / macOS / Ubuntu" onChange={(e) => setForm({ ...form, os: e.target.value })} />
                  </div>
                </>
              )}
              <div className="space-y-1">
                <Label className="text-xs">Purchase Date</Label>
                <Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Purchase Cost ₹</Label>
                <Input type="number" value={form.purchase_cost} onChange={(e) => setForm({ ...form, purchase_cost: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Current Value ₹</Label>
                <Input type="number" value={form.current_value} onChange={(e) => setForm({ ...form, current_value: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Warranty Expiry</Label>
                <Input type="date" value={form.warranty_expiry} onChange={(e) => setForm({ ...form, warranty_expiry: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Condition</Label>
                <select value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm bg-white">
                  <option>Excellent</option><option>Good</option><option>Fair</option><option>Poor</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm bg-white">
                  <option value="in_stock">In Stock</option>
                  <option value="in_use">In Use</option>
                  <option value="repair">Under Repair</option>
                  <option value="damaged">Damaged</option>
                  <option value="disposed">Disposed</option>
                </select>
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Assigned To Employee</Label>
                <select
                  value={form.assigned_to || ''}
                  onChange={(e) => {
                    const empId = e.target.value;
                    const emp = employees.find((x) => x.id === empId);
                    setForm({
                      ...form,
                      assigned_to: empId,
                      assigned_to_name: emp ? emp.name : '',
                      assigned_date: empId ? (form.assigned_date || new Date().toISOString().slice(0,10)) : '',
                      status: empId ? 'in_use' : (form.status === 'in_use' ? 'in_stock' : form.status),
                    });
                  }}
                  className="w-full border rounded px-3 py-2 text-sm bg-white"
                  data-testid="asset-assigned-to-select">
                  <option value="">— Not assigned (keep in stock) —</option>
                  {employees.filter((e) => e.is_active !== false).map((e) => (
                    <option key={e.id} value={e.id}>{e.name} ({e.emp_id})</option>
                  ))}
                </select>
                {form.assigned_to && (
                  <div className="text-[11px] text-gray-500 mt-1">
                    Assigned on:&nbsp;
                    <input type="date" value={form.assigned_date || ''}
                      onChange={(e) => setForm({ ...form, assigned_date: e.target.value })}
                      className="border rounded px-2 py-0.5 text-[11px]" />
                  </div>
                )}
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Notes</Label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm min-h-[60px]" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={cancel}>Cancel</Button>
              <Button onClick={save} data-testid="asset-save-btn">{editing.id ? 'Update' : 'Create'}</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Assign modal */}
      {assignFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setAssignFor(null)}>
          <Card className="w-full max-w-md bg-white p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-1">Assign Asset</h2>
            <p className="text-xs text-gray-600 mb-3">
              {assignFor.name} {assignFor.assigned_to_name ? `(currently with ${assignFor.assigned_to_name})` : '(unassigned)'}
            </p>
            <div className="max-h-[400px] overflow-y-auto space-y-1">
              {assignFor.assigned_to && (
                <button onClick={() => doAssign('')} className="w-full text-left p-2 rounded hover:bg-red-50 border border-red-200 text-red-700 text-sm font-semibold" data-testid="unassign-btn">
                  ✗ Unassign / Return to stock
                </button>
              )}
              {employees.filter((e) => e.is_active !== false).map((e) => (
                <button key={e.id} onClick={() => doAssign(e.id)}
                  className="w-full text-left p-2 rounded hover:bg-gray-100 text-sm flex items-center justify-between"
                  data-testid={`assign-to-${e.emp_id}`}>
                  <span>{e.name}</span>
                  <span className="text-[10px] font-mono text-gray-500">{e.emp_id}</span>
                </button>
              ))}
            </div>
            <Button variant="outline" className="w-full mt-3" onClick={() => setAssignFor(null)}>Cancel</Button>
          </Card>
        </div>
      )}

      {/* History modal */}
      {historyFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setHistoryFor(null)}>
          <Card className="w-full max-w-xl bg-white p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold">Assignment History — {historyFor.name}</h2>
              <button onClick={() => setHistoryFor(null)} className="text-2xl leading-none px-2">×</button>
            </div>
            {history.length === 0 ? (
              <p className="text-sm text-gray-600 text-center py-6">No history yet.</p>
            ) : (
              <table className="w-full text-xs">
                <thead style={{ background: 'var(--dx-bg)' }}>
                  <tr>
                    <th className="text-left px-2 py-1.5">When</th>
                    <th className="text-left px-2 py-1.5">Action</th>
                    <th className="text-left px-2 py-1.5">From</th>
                    <th className="text-left px-2 py-1.5">To</th>
                    <th className="text-left px-2 py-1.5">By</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-t">
                      <td className="px-2 py-1.5 font-mono text-[10px]">{new Date(h.performed_at).toLocaleString()}</td>
                      <td className="px-2 py-1.5 uppercase font-semibold text-[10px]" style={{ color: h.action === 'unassign' ? '#dc2626' : '#16a34a' }}>{h.action}</td>
                      <td className="px-2 py-1.5">{h.from_employee_name || '—'}</td>
                      <td className="px-2 py-1.5">{h.to_employee_name || '—'}</td>
                      <td className="px-2 py-1.5 text-gray-500">{h.performed_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default AssetsPage;
